import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { Store, Package, ArrowLeft, Loader2, ChevronLeft, ChevronRight, ShoppingCart, Calendar, Search, X } from 'lucide-react';
import { amplifyApi } from '../services/amplifyApi';
import CheckoutModal from '../components/CheckoutModal';
import ColorSwatches from '../components/ColorSwatches';

// Helper function to categorize products by title (same as ProductGallery)
const categorizeProduct = (product) => {
  const title = (product.title || '').toLowerCase();

  if (title.includes('t-shirt') || title.includes('tee ') || title.includes(' tee') ||
      (title.includes('unisex') && !title.includes('short') && !title.includes('jogger') && !title.includes('sweatpant'))) {
    return 'T-Shirts';
  }
  if (title.includes('hoodie') || title.includes('sweatshirt') || title.includes('crewneck') || title.includes('pullover')) {
    return 'Hoodies';
  }
  if (title.includes('short') || title.includes('jogger') || title.includes('sweatpant') ||
      title.includes('legging') || title.includes('pants') || title.includes('skirt')) {
    return 'Bottoms';
  }
  if (title.includes('mug') || title.includes('tumbler') || title.includes('bottle') || title.includes('cup')) {
    return 'Drinkware';
  }
  if (title.includes('poster') || title.includes('canvas') || title.includes('framed') ||
      title.includes('wall art') || (title.includes('print') && !title.includes('all-over'))) {
    return 'Wall Art';
  }
  if (title.includes('phone') || title.includes('case') || title.includes('hat') ||
      title.includes('cap') || title.includes('sticker') || title.includes('mousepad') ||
      title.includes('keychain') || title.includes('magnet') || title.includes('badge') ||
      title.includes('patch') || title.includes('pin')) {
    return 'Accessories';
  }
  if (title.includes('tote') || title.includes('bag') || title.includes('backpack') || title.includes('pouch')) {
    return 'Bags';
  }
  if (title.includes('pillow') || title.includes('blanket') || title.includes('towel') ||
      title.includes('shower curtain') || title.includes('curtain') || title.includes('rug') ||
      title.includes('mat') || title.includes('coaster') || title.includes('clock') ||
      title.includes('ornament') || title.includes('flag') || title.includes('tapestry')) {
    return 'Home';
  }
  if (title.includes('apron') || title.includes('cutting board') || title.includes('oven mitt') ||
      title.includes('pot holder') || title.includes('placemat') || title.includes('napkin')) {
    return 'Kitchen';
  }
  if (title.includes('baby') || title.includes('onesie') || title.includes('bib') ||
      title.includes('kids') || title.includes('toddler') || title.includes('infant')) {
    return 'Kids';
  }
  return 'Other';
};

const PRODUCTS_PER_PAGE = 12;

const ShopPage = () => {
  const { userId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [shopData, setShopData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);

  // Checkout state
  const [checkoutProduct, setCheckoutProduct] = useState(null);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);

  // Color/image tracking state (like ProductGallery)
  const [productImages, setProductImages] = useState({}); // Track current image for each product
  const [selectedColors, setSelectedColors] = useState({}); // Track selected color per product for checkout
  const [loadingImages, setLoadingImages] = useState({}); // Track which product images are loading

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  // Tab scroll state
  const tabScrollRef = useRef(null);

  const currentPage = parseInt(searchParams.get('page')) || 1;

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset to page 1 when search or category changes
  useEffect(() => {
    if (currentPage !== 1 && (debouncedSearch || activeCategory !== 'all')) {
      setSearchParams({ page: '1' });
    }
  }, [debouncedSearch, activeCategory]);

  // Get all products from shopData (all pages for filtering)
  const allProducts = shopData?.products || [];

  // Compute available categories from the shop's products
  const availableCategories = useMemo(() => {
    if (!allProducts.length) return [];

    const categoryCounts = {};
    allProducts.forEach(product => {
      const category = categorizeProduct(product);
      categoryCounts[category] = (categoryCounts[category] || 0) + 1;
    });

    // Sort categories by count (descending), then alphabetically
    return Object.entries(categoryCounts)
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([name, count]) => ({ name, count }));
  }, [allProducts]);

  // Filter products based on search and category
  const filteredProducts = useMemo(() => {
    let result = allProducts;

    // Filter by category
    if (activeCategory !== 'all') {
      result = result.filter(p => categorizeProduct(p) === activeCategory);
    }

    // Filter by search
    if (debouncedSearch) {
      const searchLower = debouncedSearch.toLowerCase();
      result = result.filter(p =>
        (p.title || '').toLowerCase().includes(searchLower) ||
        (p.description || '').toLowerCase().includes(searchLower)
      );
    }

    return result;
  }, [allProducts, activeCategory, debouncedSearch]);

  // Paginate filtered products locally
  const paginatedProducts = useMemo(() => {
    const startIndex = (currentPage - 1) * PRODUCTS_PER_PAGE;
    return filteredProducts.slice(startIndex, startIndex + PRODUCTS_PER_PAGE);
  }, [filteredProducts, currentPage]);

  // Compute pagination for filtered results
  const filteredPagination = useMemo(() => ({
    page: currentPage,
    totalPages: Math.ceil(filteredProducts.length / PRODUCTS_PER_PAGE),
    totalProducts: filteredProducts.length
  }), [filteredProducts.length, currentPage]);

  // Adaptive UI thresholds
  const showSearch = allProducts.length >= 6;
  const showCategories = availableCategories.length >= 2;

  useEffect(() => {
    const fetchShop = async () => {
      setIsLoading(true);
      setError(null);
      setNotFound(false);

      try {
        // Fetch all products once - frontend handles filtering and pagination
        const response = await amplifyApi.getShop(userId);

        if (response.success) {
          setShopData(response);
        } else if (response.notFound) {
          setNotFound(true);
        } else {
          setError(response.error || 'Failed to load shop');
        }
      } catch (err) {
        console.error('Failed to fetch shop:', err);
        setError('Failed to load shop. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    if (userId) {
      fetchShop();
    }
  }, [userId]); // Only re-fetch when userId changes, not page

  const handlePageChange = (newPage) => {
    setSearchParams({ page: newPage.toString() });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBuy = (product) => {
    setCheckoutProduct(product);
    setIsCheckoutOpen(true);
  };

  const handleCheckoutClose = () => {
    setIsCheckoutOpen(false);
    setCheckoutProduct(null);
  };

  // Handle color swatch selection - update displayed image
  const handleImageChange = (productId, newImageSrc, colorGroup) => {
    // Set loading state immediately when color changes
    setLoadingImages(prev => ({
      ...prev,
      [productId]: true
    }));
    setProductImages(prev => ({
      ...prev,
      [productId]: newImageSrc
    }));
    // Track selected color for checkout
    if (colorGroup?.name) {
      setSelectedColors(prev => ({
        ...prev,
        [productId]: colorGroup.name
      }));
    }
  };

  // Handle image load complete - clear loading state
  const handleImageLoad = (productId) => {
    setLoadingImages(prev => ({
      ...prev,
      [productId]: false
    }));
  };

  // Get current image for a product (color-selected or default)
  const getCurrentImage = (product) => {
    // If user selected a specific color, use that image
    if (productImages[product.id]) {
      return productImages[product.id];
    }
    // Otherwise use the default image
    return getProductImage(product);
  };

  // Get price range from variants
  const getPriceRange = (product) => {
    if (!product?.variants || product.variants.length === 0) {
      return product?.price ? `$${product.price}` : 'Price TBD';
    }

    const enabledVariants = product.variants.filter(v => v.is_enabled);
    if (enabledVariants.length === 0) {
      return product?.price ? `$${product.price}` : 'Price TBD';
    }

    const prices = enabledVariants.map(v => v.price).filter(p => p != null);
    if (prices.length === 0) return 'Price TBD';

    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);

    if (minPrice === maxPrice) {
      return `$${minPrice.toFixed(2)}`;
    }
    return `$${minPrice.toFixed(2)} - $${maxPrice.toFixed(2)}`;
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  // Get best product image (user-selected mockup first, then first image, then fallback)
  const getProductImage = (product) => {
    // Priority 1: mainImage (user-selected mockup)
    if (product.mainImage) return product.mainImage;
    // Priority 2: First image from images array
    if (product.images && Array.isArray(product.images) && product.images.length > 0) {
      const firstImage = product.images[0];
      if (firstImage?.src) return firstImage.src;
    }
    // Priority 3: mockupUrl (default mockup)
    if (product.mockupUrl) return product.mockupUrl;
    // Fallback
    return null;
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Loading shop...</p>
        </div>
      </div>
    );
  }

  // Not found state
  if (notFound) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <Store className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Shop Not Found</h1>
          <p className="text-slate-600 mb-6">This shop may not exist or is not currently available.</p>
          <Link
            to="/studio-x7k9m2"
            className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Browse All Products
          </Link>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !shopData) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <Store className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Something Went Wrong</h1>
          <p className="text-slate-600 mb-6">{error || 'Failed to load shop. Please try again.'}</p>
          <Link
            to="/studio-x7k9m2"
            className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Browse All Products
          </Link>
        </div>
      </div>
    );
  }

  const { creator } = shopData;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/studio-x7k9m2" className="flex items-center gap-2.5">
            <div className="relative w-9 h-9">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl rotate-3 shadow-lg shadow-blue-500/25"></div>
              <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl flex items-center justify-center">
                <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2L2 7l10 5 10-5-10-5z" />
                  <path d="M2 17l10 5 10-5" />
                  <path d="M2 12l10 5 10-5" />
                </svg>
              </div>
            </div>
            <div className="flex flex-col">
              <h1 className="text-xl font-bold tracking-tight text-slate-900">
                Merch<span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-600">Like</span>
              </h1>
              <span className="text-[10px] font-medium text-slate-400 -mt-1 tracking-wide">Merch like magic.</span>
            </div>
          </Link>
          <Link
            to="/studio-x7k9m2"
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm font-medium hidden sm:inline">Back to Store</span>
          </Link>
        </div>
      </header>

      {/* Shop Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Creator Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 mb-8">
          <div className="flex items-start gap-4">
            {/* Avatar placeholder */}
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
              <Store className="w-8 h-8 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold text-slate-900">
                Creator Shop
              </h1>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm text-slate-500">
                <span className="flex items-center gap-1.5">
                  <Calendar className="w-4 h-4" />
                  Member since {formatDate(creator.memberSince)}
                </span>
                <span className="flex items-center gap-1.5">
                  <Package className="w-4 h-4" />
                  {creator.productCount} {creator.productCount === 1 ? 'product' : 'products'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filter Section - Adaptive UI */}
        {(showSearch || showCategories) && allProducts.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-6">
            {/* Search Bar - Show when 6+ products */}
            {showSearch && (
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-10 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>
            )}

            {/* Category Tabs - Show when 2+ categories */}
            {showCategories && (
              <div className="relative">
                <div
                  ref={tabScrollRef}
                  className="flex gap-2 overflow-x-auto scrollbar-hide pb-1"
                  style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                >
                  {/* All tab */}
                  <button
                    onClick={() => setActiveCategory('all')}
                    className={`flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      activeCategory === 'all'
                        ? 'bg-blue-600 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    All ({allProducts.length})
                  </button>
                  {/* Category tabs */}
                  {availableCategories.map(({ name, count }) => (
                    <button
                      key={name}
                      onClick={() => setActiveCategory(name)}
                      className={`flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        activeCategory === name
                          ? 'bg-blue-600 text-white'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      {name} ({count})
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Active filters summary */}
            {(debouncedSearch || activeCategory !== 'all') && (
              <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
                <span className="text-sm text-slate-500">
                  {filteredProducts.length} {filteredProducts.length === 1 ? 'product' : 'products'} found
                  {debouncedSearch && <span className="ml-1">for "{debouncedSearch}"</span>}
                  {activeCategory !== 'all' && <span className="ml-1">in {activeCategory}</span>}
                </span>
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setActiveCategory('all');
                  }}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  Clear filters
                </button>
              </div>
            )}
          </div>
        )}

        {/* Products Grid */}
        {allProducts.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-12 text-center">
            <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">No Products Yet</h2>
            <p className="text-slate-600 mb-6">This creator hasn't published any products yet.</p>
            <Link
              to="/studio-x7k9m2"
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Browse All Products
            </Link>
          </div>
        ) : paginatedProducts.length === 0 ? (
          /* No results from filtering */
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-12 text-center">
            <Search className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">No Products Found</h2>
            <p className="text-slate-600 mb-6">
              No products match your search
              {debouncedSearch && <span> for "{debouncedSearch}"</span>}
              {activeCategory !== 'all' && <span> in {activeCategory}</span>}
            </p>
            <button
              onClick={() => {
                setSearchQuery('');
                setActiveCategory('all');
              }}
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Clear Filters
            </button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {paginatedProducts.map((product) => (
                <div
                  key={product.id}
                  className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-md transition-all"
                >
                  {/* Product Image */}
                  <Link to={`/products/${product.id}`} className="block">
                    <div className="aspect-[3/4] bg-slate-100 relative overflow-hidden">
                      <img
                        src={getCurrentImage(product) || 'https://via.placeholder.com/400x500/f3f4f6/9ca3af?text=No+Image'}
                        alt={product.title}
                        className={`w-full h-full object-cover hover:scale-105 transition-all duration-300 ${
                          loadingImages[product.id] ? 'opacity-50' : 'opacity-100'
                        }`}
                        onLoad={() => handleImageLoad(product.id)}
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/400x500/f3f4f6/9ca3af?text=No+Image';
                          handleImageLoad(product.id);
                        }}
                      />
                      {/* Loading spinner overlay */}
                      {loadingImages[product.id] && (
                        <div className="absolute inset-0 flex items-center justify-center bg-white/30">
                          <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                        </div>
                      )}
                    </div>
                  </Link>

                  {/* Product Info */}
                  <div className="p-4">
                    <Link to={`/products/${product.id}`}>
                      <h3 className="font-medium text-slate-900 line-clamp-2 hover:text-blue-600 transition-colors">
                        {product.title}
                      </h3>
                    </Link>
                    <p className="text-lg font-bold text-blue-600 mt-2">
                      {getPriceRange(product)}
                    </p>

                    {/* Color Swatches */}
                    <div className="mt-3">
                      <ColorSwatches
                        product={product}
                        api={amplifyApi}
                        onImageChange={(newImageSrc, colorGroup) => handleImageChange(product.id, newImageSrc, colorGroup)}
                        images={product.images || []}
                      />
                    </div>

                    {/* Buy Button */}
                    <button
                      onClick={() => handleBuy(product)}
                      className="w-full mt-4 flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                    >
                      <ShoppingCart className="w-4 h-4" />
                      Buy Now
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {filteredPagination.totalPages > 1 && (
              <div className="mt-8 flex items-center justify-center gap-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </button>

                <div className="flex items-center gap-1">
                  {Array.from({ length: filteredPagination.totalPages }, (_, i) => i + 1)
                    .filter(page => {
                      // Show first, last, current, and adjacent pages
                      return page === 1 ||
                        page === filteredPagination.totalPages ||
                        Math.abs(page - currentPage) <= 1;
                    })
                    .map((page, idx, arr) => {
                      // Add ellipsis if there's a gap
                      const showEllipsis = idx > 0 && page - arr[idx - 1] > 1;
                      return (
                        <React.Fragment key={page}>
                          {showEllipsis && (
                            <span className="px-2 text-slate-400">...</span>
                          )}
                          <button
                            onClick={() => handlePageChange(page)}
                            className={`w-10 h-10 flex items-center justify-center text-sm font-medium rounded-lg transition-colors ${
                              page === currentPage
                                ? 'bg-blue-600 text-white'
                                : 'text-slate-700 bg-white border border-slate-300 hover:bg-slate-50'
                            }`}
                          >
                            {page}
                          </button>
                        </React.Fragment>
                      );
                    })}
                </div>

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === filteredPagination.totalPages}
                  className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* Product count */}
            <div className="mt-4 text-center text-sm text-slate-500">
              Showing {(currentPage - 1) * PRODUCTS_PER_PAGE + 1}-{Math.min(currentPage * PRODUCTS_PER_PAGE, filteredPagination.totalProducts)} of {filteredPagination.totalProducts} products
            </div>
          </>
        )}

        {/* Back to Gallery Link */}
        <div className="mt-8 text-center">
          <Link
            to="/studio-x7k9m2"
            className="inline-flex items-center gap-2 text-slate-600 hover:text-blue-600 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to All Products
          </Link>
        </div>
      </main>

      {/* Checkout Modal */}
      <CheckoutModal
        product={checkoutProduct}
        isOpen={isCheckoutOpen}
        onClose={handleCheckoutClose}
        onOrderComplete={() => {
          handleCheckoutClose();
        }}
        api={amplifyApi}
        initialColor={checkoutProduct ? selectedColors[checkoutProduct.id] : null}
        initialImage={checkoutProduct ? productImages[checkoutProduct.id] : null}
      />
    </div>
  );
};

export default ShopPage;
